import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse, httpResource } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LicencePortfolioService } from '../../Modules/Licences/Services/licence-portfolio.service';
import { OrganizationLicenceService } from '../../Modules/Organizations/Services/organization-licence.service';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { TranslateService } from '@ngx-translate/core';
import { OperatorFacade } from '../operator.facade';
import {
  DEFAULT_PAGE_SIZE,
  FIRST_PAGE,
  MAX_PAGE_SIZE,
  VALUE_UNRESOLVED,
} from '../../Consts/backoffice.const';
import { ROLE_BILLING, ROLE_NOTES } from '../../Consts/organization-detail.const';
import {
  ADJUST_APPLIED_MESSAGE,
  ASSIGN_APPLIED_MESSAGE,
  LICENCE_CANCELLED_MESSAGE,
  LICENCE_HELD_BY_KEY,
  LICENCE_RESUMED_MESSAGE,
  RELEASE_APPLIED_MESSAGE,
  RESUME_APPLIED_MESSAGE,
} from '../../Consts/licences.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { Licence } from '../../Models/backoffice/licence.model';
import { LicenceComponent } from '../../Models/backoffice/licence-component.model';
import { OrganizationSummary } from '../../Models/backoffice/organization-summary.model';
import { TenantUser } from '../../Models/backoffice/tenant-user.model';
import {
  LicenceAdjustPayload,
  LicenceFilters,
  LicenceResumePayload,
} from '../../Modules/Organizations/Interfaces/licence-filters.interface';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationListResponse } from '../../Interfaces/api/backoffice/organization.interface';
import { TenantUserListResponse } from '../../Interfaces/api/backoffice/user.interface';
import {
  LicenceConflictEnvelope,
  LicenceFreePoolResponse,
  LicencePortfolioResponse,
} from '../../Interfaces/api/backoffice/licence.interface';

@Injectable()
export class LicencePortfolioFacade {
  private readonly _portfolioService = inject(LicencePortfolioService);
  private readonly _licenceService = inject(OrganizationLicenceService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _translate = inject(TranslateService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _organizationId = signal<string>('');
  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isMutating = signal<boolean>(false);

  private readonly _filters = signal<LicenceFilters>({
    component_type: '',
    holder: '',
    state: '',
  });

  readonly canAssign = computed<boolean>(() => this._operatorFacade.atLeast(ROLE_NOTES));

  readonly canAdjust = computed<boolean>(() => this._operatorFacade.atLeast(ROLE_BILLING));

  private readonly _organizations = httpResource<LaravelDefaultResponse<OrganizationListResponse>>(
    () => this._organizationService.listRequest({}, FIRST_PAGE, MAX_PAGE_SIZE),
  );

  private readonly _portfolio = httpResource<LaravelDefaultResponse<LicencePortfolioResponse>>(() =>
    this._organizationId()
      ? this._licenceService.portfolioRequest(
          this._organizationId(),
          this._filters(),
          this._page(),
          this._pageSize(),
        )
      : undefined,
  );

  private readonly _freePool = httpResource<LaravelDefaultResponse<LicenceFreePoolResponse>>(() =>
    this._organizationId()
      ? this._portfolioService.freePoolRequest(this._organizationId())
      : undefined,
  );

  private readonly _users = httpResource<LaravelDefaultResponse<TenantUserListResponse>>(() =>
    this._organizationId() && this.canAssign()
      ? this._portfolioService.organizationUsersRequest(this._organizationId())
      : undefined,
  );

  readonly organizationId = computed<string>(() => this._organizationId());

  readonly organizations = computed<OrganizationSummary[]>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizations)).items,
  );

  readonly organizationsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._organizations.status()),
  );

  readonly organizationName = computed<string>(
    () =>
      this.organizations().find((organization) => organization.id === this._organizationId())
        ?.displayName ?? '',
  );

  readonly filters = computed<LicenceFilters>(() => this._filters());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly licences = computed<Licence[]>(() =>
    this._licenceService.toLicences(this._resourceState.valueOf(this._portfolio)),
  );

  readonly components = computed<LicenceComponent[]>(() =>
    this._licenceService.toComponents(this._resourceState.valueOf(this._portfolio)),
  );

  readonly total = computed<number>(() => this._licenceService.toTotal(this._resourceState.valueOf(this._portfolio)));

  readonly halfStateTotal = computed<number>(() =>
    this._licenceService.toHalfStateTotal(this._resourceState.valueOf(this._portfolio)),
  );

  readonly subscriptionSummary = computed<LicencePortfolioResponse['subscription']>(() =>
    this._licenceService.toSubscriptionSummary(this._resourceState.valueOf(this._portfolio)),
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._portfolio.status()),
  );

  readonly freePool = computed<LicenceFreePoolResponse | null>(() =>
    this._portfolioService.toFreePool(this._resourceState.valueOf(this._freePool)),
  );

  readonly freePoolState = computed<MetricState>(() =>
    this._resourceState.metricState(this._freePool.status()),
  );

  readonly users = computed<TenantUser[]>(() =>
    this._portfolioService.toUsers(this._resourceState.valueOf(this._users)),
  );

  readonly hasOrganization = computed<boolean>(() => this._organizationId() !== '');

  readonly hasSubscription = computed<boolean>(() => this.subscriptionSummary() !== null);

  readonly isAdjustable = computed<boolean>(
    () => this.subscriptionSummary()?.is_adjustable ?? false,
  );

  readonly hasActiveFilters = computed<boolean>(() =>
    Object.values(this._filters()).some((value) => value !== ''),
  );

  readonly hasDrift = computed<boolean>(() =>
    this.components().some((component) => component.isDrifted),
  );

  selectOrganization(organizationId: string): void {
    this._organizationId.set(organizationId);
    this._page.set(FIRST_PAGE);
  }

  applyFilters(filters: LicenceFilters): void {
    this._filters.set(filters);
    this._page.set(FIRST_PAGE);
  }

  clearFilters(): void {
    this.applyFilters({ component_type: '', holder: '', state: '' });
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._portfolio.reload();
    this._freePool.reload();
  }

  adjust(payload: LicenceAdjustPayload): void {
    this._mutate(
      this._licenceService.adjust(this._organizationId(), payload),
      ADJUST_APPLIED_MESSAGE,
    );
  }

  resumeQuantity(payload: LicenceResumePayload): void {
    this._mutate(
      this._licenceService.resumeQuantity(this._organizationId(), payload),
      RESUME_APPLIED_MESSAGE,
    );
  }

  assign(licenceId: string, userId: string, reason: string): void {
    this._mutate(
      this._portfolioService.assign(this._organizationId(), licenceId, userId, reason),
      ASSIGN_APPLIED_MESSAGE,
    );
  }

  release(licenceId: string, reason: string): void {
    this._mutate(
      this._portfolioService.release(this._organizationId(), licenceId, reason),
      RELEASE_APPLIED_MESSAGE,
    );
  }

  cancelOne(licenceId: string, reason: string): void {
    this._mutate(
      this._licenceService.cancelOne(this._organizationId(), licenceId, reason),
      LICENCE_CANCELLED_MESSAGE,
    );
  }

  resumeOne(licenceId: string, reason: string): void {
    this._mutate(
      this._licenceService.resumeOne(this._organizationId(), licenceId, reason),
      LICENCE_RESUMED_MESSAGE,
    );
  }

  private _mutate(source: Observable<unknown>, message: string): void {
    this._isMutating.set(true);

    source.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: () => this._afterWrite(message),
      error: (error: unknown) => this._failMutation(error),
    });
  }

  private _afterWrite(message: string): void {
    this._isMutating.set(false);
    this.reload();
    this._users.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._conflictMessage(error), { type: 'error' });
  }

  private _conflictMessage(error: unknown): string {
    const base = this._translate.instant(this._errorHandler.messageFor(error));
    const holder = this._heldBy(error);

    return holder === null ? base : this._translate.instant(LICENCE_HELD_BY_KEY, { base, holder });
  }

  private _heldBy(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const envelope = error.error as LicenceConflictEnvelope | null;
    const held = envelope?.data?.held_by;

    if (!held) {
      return null;
    }

    return held.holder_name || held.holder_email || held.holder_id || VALUE_UNRESOLVED;
  }
}
